import type { OpenRouterKeyInfo } from './openRouterService';
import type { OpenRouterOperationalStatus } from './openRouterAccountObservabilityService';

export type OpenRouterHealthLevel = 'healthy' | 'degraded' | 'stale' | 'unknown';

export interface OpenRouterHealthSummary {
  level: OpenRouterHealthLevel;
  label: string;
}

interface OpenRouterHealthInput {
  operationalStatus?: OpenRouterOperationalStatus | null;
  keyInfo?: OpenRouterKeyInfo | null;
  configured?: boolean | null;
  error: string | null;
  lowKeyCapUsd?: number;
}

function formatUsd(value: number): string {
  return value >= 100 ? `$${Math.round(value)}` : `$${value.toFixed(2)}`;
}

/**
 * Derive the browser-visible OpenRouter state without pretending that a key
 * cap is the account balance. Until a server-side management-key monitor is
 * configured, a valid/uncapped inference key remains `unknown`, never green.
 */
export function deriveOpenRouterHealth({
  operationalStatus,
  keyInfo,
  configured,
  error,
  lowKeyCapUsd = 10,
}: OpenRouterHealthInput): OpenRouterHealthSummary {
  if (error) {
    return { level: 'unknown', label: 'account status check failed' };
  }

  if (operationalStatus) {
    return {
      level: operationalStatus.status,
      label: operationalStatus.label,
    };
  }

  if (configured === false) {
    return { level: 'unknown', label: 'no operator key; account credit not monitored' };
  }

  if (configured === null || !keyInfo) {
    return { level: 'unknown', label: 'checking key; account credit not monitored' };
  }

  if (keyInfo.limitRemaining !== null && keyInfo.limitRemaining < lowKeyCapUsd) {
    return {
      level: 'degraded',
      label: `${formatUsd(keyInfo.limitRemaining)} key cap left; account credit not monitored`,
    };
  }

  const keyCapLabel = keyInfo.limitRemaining === null
    ? `${formatUsd(keyInfo.usage)} key spend, uncapped`
    : `${formatUsd(keyInfo.limitRemaining)} key cap left`;

  return {
    level: 'unknown',
    label: `${keyCapLabel}; account credit not monitored`,
  };
}
